import ExcelJS from 'exceljs';
import { CategoryTree } from '../types/category';
import { formatMetricaDate } from './dateUtils';
import {
  buildImportListTables,
  IMPORT_DATA_SHEET,
  IMPORT_EXAMPLE_VENDOR,
  IMPORT_HEADER_ORDER,
  IMPORT_HEADERS,
} from './expenseImport';

const LISTS_SHEET = '_lists';
const LAST_DATA_ROW = 2501;

const HEADER_COMMENTS: Partial<Record<keyof typeof IMPORT_HEADERS, string>> = {
  vendorName: 'בחרו מהרשימה או הקלידו שם חדש',
  documentDate: 'dd-mmm-yy',
  category2: 'לפי L1 בשורה',
  category3: 'לפי L2 בשורה',
  projectTag: 'בחרו מהרשימה או הקלידו תג חדש',
};

const colLetter = (col: number): string => {
  let n = col;
  let letter = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    n = Math.floor((n - 1) / 26);
  }
  return letter;
};

const downloadBlob = (buffer: ExcelJS.Buffer, filename: string) => {
  const blob = new Blob([buffer as unknown as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const writeColumn = (sheet: ExcelJS.Worksheet, col: number, values: string[]) => {
  values.forEach((value, index) => {
    sheet.getCell(index + 1, col).value = value;
  });
};

const listFormula = (col: number, count: number): string =>
  count > 0 ? `'${LISTS_SHEET}'!$${colLetter(col)}$1:$${colLetter(col)}$${count}` : '';

const dependentListFormula = (
  valueCol: number,
  keyCol: number,
  keyCell: string
): string =>
  `OFFSET('${LISTS_SHEET}'!$${colLetter(valueCol)}$1,MATCH(${keyCell},'${LISTS_SHEET}'!$${colLetter(keyCol)}:$${colLetter(keyCol)},0)-1,0,COUNTIF('${LISTS_SHEET}'!$${colLetter(keyCol)}:$${colLetter(keyCol)},${keyCell}),1)`;

export const downloadExpenseImportTemplate = async (
  tree: CategoryTree,
  fileDate: string,
  vendors: string[] = [],
  projectTags: string[] = []
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Metrica';
  workbook.created = new Date();

  const lists = buildImportListTables(tree);
  const helper = workbook.addWorksheet(LISTS_SHEET, { state: 'veryHidden' });
  writeColumn(helper, 1, lists.l1);
  writeColumn(helper, 2, lists.l2Parents);
  writeColumn(helper, 3, lists.l2Names);
  writeColumn(helper, 4, lists.l3Keys);
  writeColumn(helper, 5, lists.l3Names);
  writeColumn(helper, 6, vendors);
  writeColumn(helper, 7, projectTags);

  const sheet = workbook.addWorksheet(IMPORT_DATA_SHEET);
  sheet.views = [{ rightToLeft: true, state: 'frozen', ySplit: 1 }];
  sheet.columns = IMPORT_HEADER_ORDER.map(() => ({ width: 18 }));

  IMPORT_HEADER_ORDER.forEach((key, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = IMPORT_HEADERS[key];
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D6433' } };
    cell.alignment = { horizontal: 'right', readingOrder: 'rtl' };
    const comment = HEADER_COMMENTS[key];
    if (comment) cell.note = comment;
  });

  const vendorCol = IMPORT_HEADER_ORDER.indexOf('vendorName') + 1;
  const netCol = IMPORT_HEADER_ORDER.indexOf('netAmount') + 1;
  const vatCol = IMPORT_HEADER_ORDER.indexOf('vatAmount') + 1;
  const totalCol = IMPORT_HEADER_ORDER.indexOf('totalAmount') + 1;
  const l1Col = IMPORT_HEADER_ORDER.indexOf('category1') + 1;
  const l2Col = IMPORT_HEADER_ORDER.indexOf('category2') + 1;
  const l3Col = IMPORT_HEADER_ORDER.indexOf('category3') + 1;
  const dateCol = IMPORT_HEADER_ORDER.indexOf('documentDate') + 1;
  const tagCol = IMPORT_HEADER_ORDER.indexOf('projectTag') + 1;
  const l1Cell = (row: number) => `$${colLetter(l1Col)}${row}`;
  const l2Cell = (row: number) => `$${colLetter(l2Col)}${row}`;
  const l3KeyCell = (row: number) => `${l1Cell(row)}&"|"&${l2Cell(row)}`;

  const firstL1 = lists.l1[0] || '';
  const firstL2 = lists.l2Names.find((name, index) => lists.l2Parents[index] === firstL1 && name) || '';
  const firstL3Key = `${firstL1}|${firstL2}`;
  const firstL3 = lists.l3Names.find((name, index) => lists.l3Keys[index] === firstL3Key && name) || '';

  sheet.getCell(2, vendorCol).value = IMPORT_EXAMPLE_VENDOR;
  sheet.getCell(2, netCol).value = 1934.19;
  sheet.getCell(2, vatCol).value = 328.81;
  sheet.getCell(2, totalCol).value = 2263;
  sheet.getCell(2, l1Col).value = firstL1;
  sheet.getCell(2, l2Col).value = firstL2;
  sheet.getCell(2, l3Col).value = firstL3;
  sheet.getCell(2, dateCol).value = formatMetricaDate(new Date().toISOString());
  sheet.getCell(2, tagCol).value = projectTags[0] || '';

  const l1Range = listFormula(1, lists.l1.length);
  const vendorRange = listFormula(6, vendors.length);
  const tagRange = listFormula(7, projectTags.length);
  const hasL2 = lists.l2Names.length > 0;
  const hasL3 = lists.l3Names.length > 0;

  for (let row = 2; row <= LAST_DATA_ROW; row += 1) {
    if (vendorRange) {
      sheet.getCell(row, vendorCol).dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [vendorRange],
        showErrorMessage: false,
      };
    }
    if (tagRange) {
      sheet.getCell(row, tagCol).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [tagRange],
        showErrorMessage: false,
      };
    }
    if (l1Range) {
      sheet.getCell(row, l1Col).dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [l1Range],
        showErrorMessage: true,
        error: 'יש לבחור קטגוריה L1 מהרשימה',
      };
    }
    if (hasL2) {
      sheet.getCell(row, l2Col).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [dependentListFormula(3, 2, l1Cell(row))],
        showErrorMessage: true,
        error: 'יש לבחור קטגוריה L2 לפי L1',
      };
    }
    if (hasL3) {
      sheet.getCell(row, l3Col).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [dependentListFormula(5, 4, l3KeyCell(row))],
        showErrorMessage: true,
        error: 'יש לבחור קטגוריה L3 לפי L2',
      };
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(buffer, `metrica-import-expenses_${fileDate}.xlsx`);
};
